import React, { Fragment, useState, useRef, useEffect } from "react";
import { showHttpError } from "../utils/library";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";

// ─────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────
const BASE_URL = `${window.location.origin}/merchant/checkout/`;
const API_BASE = WEBSERVICE;

const FEE_OPTIONS = [
    { id: "1", name: "Customer Pays" },
    { id: "0", name: "Merchant Pays" },
];

const getMerchantId = () => localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";
const getToken = () => getAuthToken() || "";
const authHeaders = () => ({
    "Content-Type": "application/json",
    "Authorization": `Bearer ${getToken()}`,
    "UUID": getAuthUuid() || "",
});

const apiGet = (url) => fetch(url, { headers: authHeaders() }).then(r => { if (!r.ok) throw { response: { status: r.status } }; return r.json(); });
const apiPost = (url, body) => fetch(url, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) }).then(r => { if (!r.ok) throw { response: { status: r.status } }; return r.json(); });

const fetchShippingProfiles = (mid) => apiGet(`${API_BASE}/api/merchants/${mid}/shipping-profiles`);
const fetchDiscountProfiles = (mid) => apiGet(`${API_BASE}/shopping/discounts?merchantId=${mid}`);
const fetchCatalogs = (mid) => apiGet(`${API_BASE}/shopping/catalogs/${mid}`);
const fetchBuyerInfoProfiles = (mid) => apiGet(`${API_BASE}/buyer-information/${mid}`);
const fetchAllProducts = (mid) => apiGet(`${API_BASE}/shopping/products?merchantId=${mid}`);
const fetchPaymentOptions = (mid) => apiGet(`${API_BASE}/billing-profile/${mid}`);
const fetchMerchantSettings = (mid) => apiPost(`${API_BASE}/merchant/FetchMerchantsettings`, { merchant_id: String(mid) });
const fetchProcessingFees = (mid) => apiGet(`${API_BASE}/processing-fee/status/${mid}`);
const fetchRedirectProfiles = (mid) => apiGet(`${API_BASE}/redirect-templates?merchantId=${mid}`);
const savePaymentLink = (p) => apiPost(`${API_BASE}/payment/create`, p);

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
function formatPrice(p) {
    if (!p) return "—";
    const amt = parseFloat(p.amount || p.basePrice || p.price || 0).toFixed(2);
    const cur = p.currency || "USD";
    if (p.priceType === "subscription" || p.priceType === "recurring") {
        const int = p.billingInterval || p.recurringInterval || "month";
        return `${cur} ${amt} / ${int}`;
    }
    return `${cur} ${amt}`;
}

// ─────────────────────────────────────────────────────────────────
// Toast
// ─────────────────────────────────────────────────────────────────
function Toast({ msg, type }) {
    if (!msg) return null;
    return (
        <div style={{ position: "fixed", bottom: "24px", right: "24px", background: type === "success" ? "#10b981" : "#ef4444", color: "#fff", padding: "12px 20px", borderRadius: "12px", fontSize: "13px", fontWeight: "600", boxShadow: "0 4px 24px rgba(0,0,0,0.25)", zIndex: 9999, display: "flex", alignItems: "center", gap: "8px", animation: "slideUp .2s ease" }}>
            <i className={`mdi ${type === "success" ? "mdi-check-circle" : "mdi-alert-circle"}`} />{msg}
        </div>
    );
}

function EmptyProfileRow({ label, color, redirectPath, btnLabel }) {
    if (!redirectPath) return (
        <div style={{ padding: "10px 14px", borderRadius: "8px", border: "1.5px dashed rgba(99,102,241,.25)", background: "transparent" }}>
            <span style={{ fontSize: "13px", color: "var(--text-color)", opacity: .45 }}>No {label.toLowerCase()} found</span>
        </div>
    );
    return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderRadius: "8px", border: "1.5px dashed rgba(99,102,241,.4)", background: "transparent", gap: 12 }}>
            <span style={{ fontSize: "13px", color: "var(--text-color)", opacity: .5 }}>No {label.toLowerCase()} found</span>
            <button type="button" onClick={() => { window.location.href = redirectPath; }}
                style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 18px", borderRadius: "8px", border: "none", background: "var(--btn-color)", color: "#fff", fontSize: "13px", fontWeight: "600", cursor: "pointer", boxShadow: "0 0 12px color-mix(in srgb,var(--btn-color) 35%,transparent)", transition: "filter .15s" }}>
                + {btnLabel || `Create ${label}`}
            </button>
        </div>
    );
}

function SubRow({ label, options, value, onChange, optional, color = "#6366f1", redirectPath, btnLabel }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 0", borderBottom: "1px solid var(--body-color-less2)", flexWrap: "wrap" }}>
            <span style={{ flex: "1 1 120px", fontSize: "13px", fontWeight: "500", color: "var(--text-color)" }}>
                {label}
                {optional && <span style={{ marginLeft: "6px", fontSize: "10px", background: "rgba(100,116,139,.12)", color: "var(--text-color)", opacity: .5, borderRadius: "4px", padding: "1px 5px" }}>optional</span>}
            </span>
            {options.length === 0
                ? <EmptyProfileRow label={label} color={color} redirectPath={redirectPath} btnLabel={btnLabel} />
                : <select value={value || ""} onChange={e => { const v = e.target.value; if (!v) { onChange(null); return; } const n = parseInt(v); onChange(isNaN(n) ? v : n); }}
                    style={{ height: "36px", background: "var(--body-color)", border: "1.5px solid var(--body-color-less2)", borderRadius: "8px", padding: "0 12px", color: "var(--text-color)", fontSize: "13px", outline: "none", cursor: "pointer", minWidth: "160px", appearance: "auto" }}>
                    <option value="">— Default —</option>
                    {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
            }
        </div>
    );
}

function SimpleQR({ value, size = 140 }) {
    return <img src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(value)}&size=${size}x${size}&margin=10`} alt="QR" width={size} height={size} style={{ borderRadius: "8px", background: "#fff", padding: "4px" }} />;
}

// ─────────────────────────────────────────────────────────────────
// ProductCatalogPicker
// ─────────────────────────────────────────────────────────────────
function ProductCatalogPicker({ products, catalogs, selectedId, onSelect, onCatalogSelect, selectedCatalogId }) {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef();

    React.useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const bycat = {};
    products.forEach(p => {
        const cid = String(p.catalogId || "uncategorised");
        if (!bycat[cid]) bycat[cid] = { name: p.catalogName || catalogs.find(c => String(c.id) === cid)?.name || "Products", items: [] };
        bycat[cid].items.push(p);
    });

    const selectedProd = products.find(p => p.id === selectedId);
    const selectedCat = catalogs.find(c => c.id === selectedCatalogId);
    const triggerLabel = selectedProd ? selectedProd.name : selectedCat ? selectedCat.name : "What are you selling?";
    const isPlaceholder = !selectedProd && !selectedCat;

    const triggerStyle = {
        width: "100%", height: 42, display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 14px", fontSize: 14, borderRadius: 8,
        border: open ? "1.5px solid var(--btn-color)" : "1.5px solid var(--body-color-less2)",
        background: "var(--body-color)", color: "var(--text-color)",
        cursor: "pointer", boxSizing: "border-box",
        boxShadow: open ? "0 0 0 3px color-mix(in srgb,var(--btn-color) 14%,transparent)" : "none",
        transition: "border-color .15s, box-shadow .15s",
        userSelect: "none",
    };

    const dropStyle = {
        position: "absolute", top: "calc(100% + 5px)", left: 0, right: 0, zIndex: 200,
        background: "var(--body-color-less)", border: "1px solid var(--body-color-less2)",
        borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        overflow: "hidden", animation: "dropFade .15s ease",
    };

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <div style={triggerStyle} onClick={() => setOpen(o => !o)}>
                <span style={{ opacity: isPlaceholder ? .4 : 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {triggerLabel}
                </span>
                <i className={`mdi ${open ? "mdi-chevron-up" : "mdi-chevron-down"}`}
                    style={{ fontSize: 16, color: "var(--text-color)", opacity: .5, flexShrink: 0, marginLeft: 8 }} />
            </div>

            {open && (
                <div style={dropStyle}>
                    {Object.entries(bycat).map(([cid, group]) => (
                        <div key={cid}>
                            <div style={{ padding: "8px 14px 4px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".7px", color: "var(--text-color)", opacity: .35 }}>
                                {group.name}
                            </div>
                            {group.items.map(p => {
                                const isSelected = p.id === selectedId;
                                return (
                                    <div key={p.id}
                                        onClick={() => { onSelect(p.id); setOpen(false); }}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", cursor: "pointer", fontSize: 14, color: isSelected ? "var(--btn-color)" : "var(--text-color)", background: isSelected ? "color-mix(in srgb,var(--btn-color) 8%,transparent)" : "transparent", transition: "background .1s" }}
                                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "color-mix(in srgb,var(--btn-color) 5%,transparent)"; }}
                                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}>
                                        <span style={{ fontWeight: isSelected ? 600 : 400 }}>{p.name}</span>
                                        {isSelected && <i className="mdi mdi-check" style={{ fontSize: 14, color: "var(--btn-color)", flexShrink: 0 }} />}
                                    </div>
                                );
                            })}
                        </div>
                    ))}

                    {catalogs.length > 0 && (
                        <>
                            <div style={{ height: 1, background: "var(--body-color-less2)", margin: "4px 0" }} />
                            <div style={{ padding: "8px 14px 4px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".7px", color: "var(--text-color)", opacity: .35 }}>
                                Catalogues
                            </div>
                            {catalogs.map(cat => {
                                const isSelCat = cat.id === selectedCatalogId;
                                return (
                                    <div key={cat.id}
                                        onClick={() => { onCatalogSelect(cat.id); setOpen(false); }}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", fontSize: 14, color: isSelCat ? "var(--btn-color)" : "var(--text-color)", background: isSelCat ? "color-mix(in srgb,var(--btn-color) 8%,transparent)" : "transparent", cursor: "pointer", transition: "background .1s" }}
                                        onMouseEnter={e => { if (!isSelCat) e.currentTarget.style.background = "color-mix(in srgb,var(--btn-color) 5%,transparent)"; }}
                                        onMouseLeave={e => { if (!isSelCat) e.currentTarget.style.background = "transparent"; }}>
                                        <span style={{ fontWeight: isSelCat ? 600 : 400 }}>{cat.name}</span>
                                        {isSelCat && <i className="mdi mdi-check" style={{ fontSize: 14, color: "var(--btn-color)", flexShrink: 0 }} />}
                                    </div>
                                );
                            })}
                        </>
                    )}

                    <div style={{ height: 1, background: "var(--body-color-less2)", margin: "4px 0" }} />
                    <div onClick={() => window.dispatchEvent(new CustomEvent("openQuickPayment"))}
                        style={{ padding: "10px 14px", fontSize: 14, fontStyle: "italic", color: "var(--btn-color)", cursor: "pointer", transition: "background .1s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "color-mix(in srgb,var(--btn-color) 6%,transparent)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        Quick Payment
                    </div>
                </div>
            )}
        </div>
    );
}

function SectionLabel({ icon, color, text, optional, noMargin }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: noMargin ? 0 : "12px" }}>
            <i className={`mdi ${icon}`} style={{ fontSize: "14px", color, opacity: .8 }} />
            <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-color)", opacity: .7 }}>{text}</span>
            {optional && <span style={{ fontSize: "10px", fontWeight: "500", background: "rgba(100,116,139,.12)", color: "var(--text-color)", opacity: .5, borderRadius: "4px", padding: "1px 6px" }}>optional</span>}
        </div>
    );
}

// Style tokens
const CARD = { background: "var(--body-color-less)", border: "1px solid var(--body-color-less2)", borderRadius: "12px", padding: "18px 20px", marginBottom: "16px" };
const RIGHT_CARD = { background: "var(--body-color-less)", border: "1px solid var(--body-color-less2)", borderRadius: "12px", padding: "18px" };
const RIGHT_LABEL = { fontSize: "10px", fontWeight: "700", textTransform: "uppercase", letterSpacing: ".7px", color: "var(--text-color)", opacity: .45, marginBottom: "12px" };
const FIELD_LABEL = { fontSize: "11px", fontWeight: "700", textTransform: "uppercase", letterSpacing: ".5px", color: "var(--text-color)", opacity: .45, marginBottom: "7px", display: "block" };

// ─────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────
export default function CreateSubscription() {
    const [selections, setSelections] = useState({
        catalog: null,
        shipping: null,
        buyerInfo: null,
        discount: null,
        paymentOption: null,
        feeHandling: null,
        redirect: null,
    });
    const [paymentName, setPaymentName] = useState("");
    const [generated, setGenerated] = useState(false);
    const [paymentId, setPaymentId] = useState("");
    const [activeTab, setActiveTab] = useState("link");
    const [buttonSize, setButtonSize] = useState("medium");
    const [htmlCode, setHtmlCode] = useState("");
    const [toast, setToast] = useState({ msg: "", type: "success" });
    const [loading, setLoading] = useState(false);
    const [customizeOpen, setCustomizeOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const textareaRef = useRef(null);

    // Product selection
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [selectedProdId, setSelectedProdId] = useState(null);
    const [selectedPriceId, setSelectedPriceId] = useState(null);

    // Profiles
    const [shippingProfiles, setShippingProfiles] = useState([]);
    const [discountProfiles, setDiscountProfiles] = useState([]);
    const [catalogProfiles, setCatalogProfiles] = useState([]);
    const [buyerInfoProfiles, setBuyerInfoProfiles] = useState([]);
    const [paymentOptionProfiles, setPaymentOptionProfiles] = useState([]);
    const [redirectProfiles, setRedirectProfiles] = useState([]);

    // ── Load profiles ──────────────────────────────────────────────
    useEffect(() => {
        const mid = getMerchantId();

        fetchShippingProfiles(mid).then(res => {
            if (res.error !== 0) return;
            const active = (res.data || []).filter(p => p.isActive === 1);
            setShippingProfiles(active.map(p => ({ id: p.id, name: p.profileName })));
        }).catch(e => showHttpError(e));

        fetchDiscountProfiles(mid).then(res => {
            if (!res.status) return;
            const map = {};
            (res.data || []).forEach(r => { if (!map[r.profileName]) map[r.profileName] = { id: r.id, name: r.profileName }; });
            setDiscountProfiles(Object.values(map));
        }).catch(e => showHttpError(e));

        fetchCatalogs(mid).then(res => {
            if (!res.status) return;
            const cats = (res.data || []).map(c => ({ id: c.id, name: c.catalogName }));
            setCatalogProfiles(cats);
        }).catch(e => showHttpError(e));

        fetchBuyerInfoProfiles(mid).then(res => {
            if (res.error !== "0") return;
            const mapped = (res.data || []).map(r => ({ id: r.id, name: r.profileName }));
            setBuyerInfoProfiles(mapped);
        }).catch(e => showHttpError(e));

        // Payment Options — getAllPaymentGateway
        fetchPaymentOptions(mid).then(res => {
            const rows = res.data || [];
            if (!rows.length) return;
            const oneTime = rows.filter(p => p.billingType === 'SINGLE')
            const mapped = oneTime.map(r => ({ id: r.id, name: r.profileName }));
            setPaymentOptionProfiles(mapped);
        }).catch(e => showHttpError(e));

        // Fee Handling — pre-select from merchant settings
        fetchProcessingFees(mid).then(res => {
            const row = res.data || [];
            const flag = row?.isProcessingFeeEnabled;
            const presel = flag === "1" || flag === 1 ? "1"
                : flag === "0" || flag === 0 ? "0"
                    : "default";
            setSelections(s => ({ ...s, feeHandling: presel }));
        }).catch(e => showHttpError(e));

        // Redirects
        fetchRedirectProfiles(mid).then(res => {
            if (res.error !== "0") return;
            const mapped = (res.data || []).map(r => ({ id: r.id, name: r.templateName }));
            setRedirectProfiles(mapped);
            const def = (res.data || []).find(r => r.isDefault === 1);
            if (def) setSelections(s => ({ ...s, redirect: def.id }));
        }).catch(e => showHttpError(e));

    }, []);

    // ── Fetch ALL products on mount ────────────────────────────────
    useEffect(() => {
        const mid = getMerchantId();
        if (!mid) return;
        setProductsLoading(true);
        fetchAllProducts(mid)
            .then(res => {
                if (!res.status) { setProducts([]); return; }
                const allProds = (res.data?.products || [])
                    .filter(p => (p.status || "").toUpperCase() === "ACTIVE")
                    .map(p => ({
                    id: p.productId,
                    name: p.name || "Unnamed",
                    catalogId: null,
                    catalogName: "",
                    imageUrl: p.imageUrl || "",
                    prices: (p.prices || []).map(pr => {
                        const defaultCur = (pr.currencies || []).find(c => c.default) || pr.currencies?.[0];
                        return {
                            id: String(pr.priceId),
                            amount: defaultCur?.amount || 0,
                            currency: defaultCur?.currency || "USD",
                            priceType: pr.priceType === "recurring" ? "subscription" : "one-time",
                            billingInterval: pr.intervalType || "monthly",
                            variant: pr.metadata?.variant
                                ? Object.entries(pr.metadata.variant).map(([k, v]) => `${k}: ${v}`).join(" / ")
                                : "",
                            currencies: pr.currencies || [],
                        };
                    }),
                }));
                // Only keep products that have at least one subscription/recurring price
                const filteredProds = allProds.filter(p =>
                    (p.prices || []).some(pr => pr.priceType === "subscription" || pr.priceType === "recurring")
                );
                setProducts(filteredProds);
            })
            .catch(() => setProducts([]))
            .finally(() => setProductsLoading(false));
    }, []);

    const handleProductChange = (pid) => {
        setSelectedProdId(pid);
        const prod = products.find(p => p.id === pid);
        const subPrices = (prod?.prices || []).filter(p => p.priceType === "subscription" || p.priceType === "recurring");
        setSelectedPriceId(subPrices.length > 0 ? subPrices[0].id : null);
        if (prod?.catalogId) setSelections(s => ({ ...s, catalog: prod.catalogId }));
    };

    const selectedProduct = products.find(p => (p.id || p.productId) === selectedProdId);
    const selectedPrices = selectedProduct?.prices || [];
    const selectedPrice = selectedPrices.find(p => p.id === selectedPriceId);

    const showMsg = (type, msg) => { setToast({ msg, type }); setTimeout(() => setToast({ msg: "", type: "success" }), 3000); };

    // ── Generate ───────────────────────────────────────────────────
    const handleGenerate = async () => {
        if (!selectedProdId && !selections.catalog) { showMsg("error", "Please select a Product or Catalogue"); return; }
        setLoading(true);
        try {
            const isProductMode = !!selectedProdId;
            const effectiveDiscountId = selections.discount || 0;
            const discountProfileName =
                discountProfiles.find(
                    (p) => String(p.id) === String(effectiveDiscountId)
                )?.name || "";
            const res = await savePaymentLink({
                merchantId: parseInt(getMerchantId()),
                paymentName: paymentName.trim(),
                paymentType: "CHECKOUT",
                ...(isProductMode
                    ? { productId: selectedProdId, priceId: selectedPriceId || null }
                    : { catalogId: selections.catalog || 0 }
                ),
                shippingProfileId: selections.shipping || 0,
                buyerProfileId: selections.buyerInfo || 0,
                discountProfileName,
                billingId: selections.paymentOption || 0,
                isProcessingFeeApplied: selections.feeHandling || 0,
                redirectId: selections.redirect || 0,
            });
            if (res.error !== "0") { showMsg("error", res.message || "Failed to create subscription"); return; }
            setPaymentId(res.id); setGenerated(true); setActiveTab("link");
            setHtmlCode(getLinkFor(res.id));
            showMsg("success", res.message || "Subscription link created!");
        } catch (e) { showMsg("error", "Failed to create subscription. Please try again."); }
        finally { setLoading(false); }
    };

    const getLinkFor = (id) => {
        const base = BASE_URL.replace(/\/+$/, "");
        return `${base}/${encodeURIComponent(String(id))}`;
    };
    const getLink = () => getLinkFor(paymentId);
    const getBtnHtml = (sz) => { const w = { small: 80, medium: 105, large: 132 }; const h = { small: 43, medium: 56, large: 70 }; return `<a href="${getLink()}" target="_blank" style="display:inline-block;width:${w[sz]}px;height:${h[sz]}px;background:#6366f1;color:#fff;border-radius:6px;text-align:center;line-height:${h[sz]}px;font-size:${sz === "small" ? 11 : 13}px;font-weight:600;text-decoration:none;">Pay Now</a>`; };
    const getHyperlink = () => `<a href="${getLink()}" target="_blank">Pay Now</a>`;
    const getQrHtml = () => `<img src="https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(getLink())}&size=200x200&margin=10" alt="Pay Now QR Code" width="200" height="200" style="border-radius:8px;" />`;
    const copyText = (t) => navigator.clipboard.writeText(t).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => showMsg("error", "Copy failed"));
    const handleTabAction = (tab) => { setActiveTab(tab); if (tab === "link") setHtmlCode(getLink()); else if (tab === "hyperlink") setHtmlCode(getHyperlink()); else if (tab === "button") setHtmlCode(getBtnHtml(buttonSize)); else if (tab === "qr") setHtmlCode(getQrHtml()); };
    const handleBtnSize = (sz) => { setButtonSize(sz); setHtmlCode(getBtnHtml(sz)); };

    // ── Profiles shown in the Configuration summary (right panel) ──
    const PROFILE_CONFIGS = [
        { key: "catalog", label: "Product Catalogue", options: catalogProfiles, color: "var(--btn-color)", icon: "mdi-package-variant", optional: false },
        { key: "paymentOption", label: "Payment Options", options: paymentOptionProfiles, color: "#6366f1", icon: "mdi-link-variant", optional: true },
        { key: "feeHandling", label: "Fee Handling", options: FEE_OPTIONS, color: "#ec4899", icon: "mdi-percent-outline", optional: true },
        { key: "buyerInfo", label: "Buyer Information", options: buyerInfoProfiles, color: "#8b5cf6", icon: "mdi-account-check-outline", optional: true },
        { key: "shipping", label: "Shipping", options: shippingProfiles, color: "#3b82f6", icon: "mdi-truck-delivery-outline", optional: true },
        { key: "discount", label: "Discounts", options: discountProfiles, color: "#22c55e", icon: "mdi-tag-multiple-outline", optional: true },
        { key: "redirect", label: "Redirects", options: redirectProfiles, color: "#14b8a6", icon: "mdi-redo-variant", optional: true },
    ];

    // ── Customize Checkout rows ────────────────────────────────────
    const CUSTOMIZE_ROWS = [
        { key: "paymentOption", label: "Payment Options", options: paymentOptionProfiles, color: "#6366f1", optional: true, redirectPath: "/billing", btnLabel: "Add Payment Option" },
        { key: "feeHandling", label: "Fee Handling", options: FEE_OPTIONS, color: "#ec4899", optional: true, redirectPath: null },
        { key: "buyerInfo", label: "Buyer Fields", options: buyerInfoProfiles, color: "#8b5cf6", optional: true, redirectPath: "/buyer-information" },
        { key: "shipping", label: "Shipping", options: shippingProfiles, color: "#3b82f6", optional: true, redirectPath: null },
        { key: "discount", label: "Discounts", options: discountProfiles, color: "#22c55e", optional: true, redirectPath: null },
        { key: "redirect", label: "Redirects", options: redirectProfiles, color: "#14b8a6", optional: true, redirectPath: "/redirects", btnLabel: "Add Redirect" },
    ];

    const oneTimePrices = selectedPrices.filter(p => p.priceType === "onetime" || p.priceType === "one-time");
    const isProductMode = !!selectedProdId;
    const canGenerate = isProductMode
        ? !!selectedPriceId
        : !!selections.catalog;

    return (
        <Fragment>
            <style>{`
                @keyframes slideUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
                @keyframes fadeIn{from{opacity:0}to{opacity:1}}
                .cp-sel:focus{border-color:var(--btn-color)!important;box-shadow:0 0 0 3px rgba(99,102,241,.14)!important;outline:none;}
                .cp-inp:focus{border-color:var(--btn-color)!important;box-shadow:0 0 0 3px rgba(99,102,241,.14)!important;outline:none;}
                .cp-tab:hover{opacity:1!important;}
                .cp-coll{overflow:hidden;transition:max-height .35s ease;}
                @keyframes dropFade{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}
                .cp-grid>*{min-width:0;overflow:hidden;}
                @media(max-width:860px){.cp-grid{grid-template-columns:1fr!important;}.cp-right{position:static!important;}.cp-bar{flex-direction:column!important;gap:8px!important;}.cp-bar-btn{width:100%!important;}}
            `}</style>
            <div className="container-scroller">
                <Sidebar />
                <Header />
                <div className="main-panel">
                    <div style={{ padding: "32px 24px 100px" }}>
                        <Toast msg={toast.msg} type={toast.type} />

                        {/* Page header */}
                        <div style={{ marginBottom: "24px" }}>
                            <h2 style={{ fontSize: "22px", fontWeight: "700", color: "var(--text-color)", margin: 0, letterSpacing: "-.3px" }}>Create Subscription</h2>
                            <p style={{ fontSize: "13px", color: "var(--text-color)", opacity: .45, margin: "6px 0 0" }}>Assemble your profiles to generate a shareable subscription link</p>
                        </div>

                        <div className="cp-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0,60fr) minmax(0,40fr)", gap: "20px", alignItems: "start", gridAutoRows: "auto" }}>

                            {/* ── LEFT ── */}
                            <div style={{ minWidth: 0, width: "100%" }}>

                                {/* Card 1 — Product + Catalogue picker */}
                                <div style={CARD}>
                                    <SectionLabel icon="mdi-package-variant" color="var(--btn-color)" text="What are you selling?" />

                                    {productsLoading ? (
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-color)", opacity: .45 }}>
                                            <i className="mdi mdi-loading mdi-spin" style={{ fontSize: 15 }} /> Loading products…
                                        </div>
                                    ) : (catalogProfiles.length === 0 && products.length === 0) ? (
                                        <EmptyProfileRow label="Products" color="var(--btn-color)" redirectPath="/product-catalog" btnLabel="Create Products" />
                                    ) : (
                                        <ProductCatalogPicker
                                            products={products}
                                            catalogs={catalogProfiles}
                                            selectedId={selectedProdId}
                                            selectedCatalogId={selections.catalog}
                                            onSelect={handleProductChange}
                                            onCatalogSelect={(catId) => {
                                                setSelections(s => ({ ...s, catalog: catId }));
                                                setSelectedProdId(null);
                                                setSelectedPriceId(null);
                                            }}
                                        />
                                    )}

                                    {/* Price */}
                                    {(selectedProduct || selections.catalog) && (
                                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--body-color-less2)" }}>
                                            <label style={FIELD_LABEL}>Price</label>
                                            {!selectedProduct ? (
                                                <div style={{ fontSize: 12, color: "var(--text-color)", opacity: .4, display: "flex", alignItems: "center", gap: 5 }}>
                                                    <i className="mdi mdi-information-outline" style={{ fontSize: 13 }} /> Select a product to see pricing options
                                                </div>
                                            ) : selectedPrices.filter(p => p.priceType === "onetime" || p.priceType === "one-time").length === 0 ? (
                                                <div style={{ fontSize: 12, color: "var(--text-color)", opacity: .4, display: "flex", alignItems: "center", gap: 5 }}>
                                                    <i className="mdi mdi-alert-outline" style={{ fontSize: 13 }} /> No one-time prices set for this product
                                                </div>
                                            ) : (
                                                <>
                                                    <select
                                                        value={selectedPriceId || ""}
                                                        onChange={e => setSelectedPriceId(e.target.value ? e.target.value : null)}
                                                        style={{ width: "auto", minWidth: 200, height: 40, padding: "0 12px", fontSize: 14, borderRadius: 8, border: "1.5px solid var(--body-color-less2)", background: "var(--body-color)", color: "var(--text-color)", outline: "none", cursor: "pointer", appearance: "auto" }}>
                                                        <option value="">— Select price —</option>
                                                        {selectedPrices.filter(p => p.priceType === "onetime" || p.priceType === "one-time").map(p => (
                                                            <option key={p.id} value={p.id}>
                                                                {formatPrice(p)}{p.variant ? ` — ${p.variant}` : ""}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    {/* Currency pills */}
                                                    {selectedPrice && (selectedPrice.currencies || []).length > 0 && (
                                                        <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                                                            {(selectedPrice.currencies || []).map((c, i) => (
                                                                <span key={i} style={{
                                                                    display: "inline-flex", alignItems: "center", gap: 5,
                                                                    padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                                                                    background: "color-mix(in srgb,var(--btn-color) 10%,transparent)",
                                                                    border: "1px solid color-mix(in srgb,var(--btn-color) 22%,transparent)",
                                                                    color: "var(--btn-color)",
                                                                }}>
                                                                    <span style={{ opacity: .6, fontSize: 11 }}>{c.currency}</span>
                                                                    <span>{parseFloat(c.amount).toLocaleString()}</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Card 2 — Payment Name */}
                                <div style={CARD}>
                                    <SectionLabel icon="mdi-text-box-outline" color="#6366f1" text="Payment Name" optional />
                                    <input className="cp-inp" type="text" placeholder="e.g. Summer Sale Checkout"
                                        value={paymentName} onChange={e => setPaymentName(e.target.value)}
                                        style={{ width: "100%", height: 40, padding: "0 14px", fontSize: 14, borderRadius: 8, border: "1.5px solid var(--body-color-less2)", background: "var(--body-color)", color: "var(--text-color)", outline: "none", boxSizing: "border-box" }} />
                                </div>

                                {/* Card 3 — Customize Checkout */}
                                <div style={CARD}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <SectionLabel icon="mdi-tune-variant" color="#8b5cf6" text="Customize Checkout" optional noMargin />
                                        <button type="button" onClick={() => setCustomizeOpen(o => !o)}
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: 5,
                                                padding: "5px 12px", borderRadius: 8,
                                                border: "1.5px solid color-mix(in srgb,#8b5cf6 35%,transparent)",
                                                background: customizeOpen ? "color-mix(in srgb,#8b5cf6 12%,transparent)" : "transparent",
                                                color: "#a78bfa", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all .15s",
                                            }}>
                                            <i className={`mdi ${customizeOpen ? "mdi-chevron-up" : "mdi-tune-variant"}`} style={{ fontSize: 13 }} />
                                            {customizeOpen ? "Hide options" : "Customise"}
                                        </button>
                                    </div>

                                    <div className="cp-coll" style={{ maxHeight: customizeOpen ? "900px" : "0" }}>
                                        <div style={{ height: 14 }} />
                                        {CUSTOMIZE_ROWS.map(row => (
                                            <SubRow
                                                key={row.key}
                                                label={row.label}
                                                color={row.color}
                                                options={row.options}
                                                value={selections[row.key]}
                                                onChange={v => setSelections(s => ({ ...s, [row.key]: v }))}
                                                optional={row.optional}
                                                redirectPath={row.redirectPath}
                                                btnLabel={row.btnLabel}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* ── RIGHT (sticky) ── */}
                            <div className="cp-right" style={{ position: "sticky", top: 24, display: "flex", flexDirection: "column", gap: 16, minWidth: 0, width: "100%" }}>

                                {/* Configuration summary */}
                                <div style={RIGHT_CARD}>
                                    <div style={RIGHT_LABEL}>Configuration</div>
                                    {PROFILE_CONFIGS.map(cfg => {
                                        const sel = cfg.options.find(o => o.id === selections[cfg.key]);
                                        return (
                                            <div key={cfg.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                                                <i className={sel ? "mdi mdi-check-circle" : "mdi mdi-circle-outline"} style={{ fontSize: 15, color: sel ? "#10b981" : "var(--text-color)", opacity: sel ? 1 : .2, flexShrink: 0 }} />
                                                <span style={{ fontSize: 13, color: "var(--text-color)", opacity: sel ? 1 : .4, flex: 1 }}>{cfg.label}</span>
                                                {sel && <span style={{ fontSize: 11, fontWeight: 600, color: cfg.color, background: `color-mix(in srgb,${cfg.color} 12%,transparent)`, borderRadius: 5, padding: "2px 7px", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sel.name}</span>}
                                            </div>
                                        );
                                    })}

                                    {/* Product + price rows */}
                                    {selectedProduct && (
                                        <div style={{ borderTop: "1px solid var(--body-color-less2)", marginTop: 6, paddingTop: 6 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                                                <i className="mdi mdi-check-circle" style={{ fontSize: 15, color: "#10b981", flexShrink: 0 }} />
                                                <span style={{ fontSize: 13, color: "var(--text-color)", flex: 1 }}>Product</span>
                                                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--btn-color)", background: "color-mix(in srgb,var(--btn-color) 12%,transparent)", borderRadius: 5, padding: "2px 7px", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedProduct.name}</span>
                                            </div>
                                            {selectedPrice && (
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                                                    <i className="mdi mdi-check-circle" style={{ fontSize: 15, color: "#10b981", flexShrink: 0 }} />
                                                    <span style={{ fontSize: 13, color: "var(--text-color)", flex: 1 }}>Price</span>
                                                    <span style={{ fontSize: 11, fontWeight: 600, color: "#f59e0b", background: "rgba(245,158,11,.1)", borderRadius: 5, padding: "2px 7px" }}>{formatPrice(selectedPrice)}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {paymentName.trim() && (
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid var(--body-color-less2)", marginTop: 6 }}>
                                            <i className="mdi mdi-check-circle" style={{ fontSize: 15, color: "#10b981" }} />
                                            <span style={{ fontSize: 13, color: "var(--text-color)", flex: 1 }}>Payment Name</span>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: "#6366f1", background: "rgba(99,102,241,.1)", borderRadius: 5, padding: "2px 7px", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{paymentName}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Output panel */}
                                {generated && (
                                    <div style={{ ...RIGHT_CARD, animation: "fadeIn .3s ease" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(16,185,129,.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                <i className="mdi mdi-check" style={{ fontSize: 14, color: "#10b981" }} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: "#10b981" }}>Payment Link Created</div>
                                                <div style={{ fontSize: 11, color: "var(--text-color)", opacity: .45 }}>ID: {paymentId}</div>
                                            </div>
                                            <button onClick={() => copyText(paymentId)} style={{ marginLeft: "auto", padding: "4px 10px", borderRadius: 6, border: "1px solid color-mix(in srgb,var(--btn-color) 30%,transparent)", background: "transparent", color: "var(--btn-color)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                                                <i className="mdi mdi-content-copy" /> Copy ID
                                            </button>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 8, border: "1px solid var(--body-color-less2)", background: "var(--body-color)", marginBottom: 12 }}>
                                            <span style={{ flex: 1, fontSize: 11, fontFamily: "monospace", color: "#818cf8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getLink()}</span>
                                            <button onClick={() => copyText(getLink())} style={{ flexShrink: 0, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--body-color-less2)", background: copied ? "#10b981" : "transparent", color: copied ? "#fff" : "var(--btn-color)", fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all .2s" }}>{copied ? "Copied ✓" : "Copy"}</button>
                                            <button onClick={() => window.open(getLink(), "_blank")} style={{ flexShrink: 0, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--body-color-less2)", background: "transparent", color: "var(--text-color)", fontSize: 11, cursor: "pointer", opacity: .6 }}><i className="mdi mdi-open-in-new" /></button>
                                        </div>
                                        <div style={{ display: "flex", background: "var(--body-color)", borderRadius: 9, padding: 3, border: "1px solid var(--body-color-less2)", gap: 3, marginBottom: 12 }}>
                                            {[["link", "mdi-link-variant", "Link"], ["button", "mdi-gesture-tap-button", "Button"], ["hyperlink", "mdi-link", "Hyperlink"], ["qr", "mdi-qrcode", "QR"]].map(([k, ic, lb]) => (
                                                <button key={k} className="cp-tab" type="button" onClick={() => handleTabAction(k)}
                                                    style={{ flex: 1, padding: "7px 4px", borderRadius: 7, border: "none", background: activeTab === k ? "var(--btn-color)" : "transparent", color: activeTab === k ? "#fff" : "var(--text-color)", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4, opacity: activeTab === k ? 1 : .55, transition: "all .15s" }}>
                                                    <i className={`mdi ${ic}`} style={{ fontSize: 13 }} />{lb}
                                                </button>
                                            ))}
                                        </div>
                                        {activeTab === "button" && (
                                            <div style={{ marginBottom: 10, display: "flex", gap: 6 }}>
                                                {[["small", "80×43"], ["medium", "105×56"], ["large", "132×70"]].map(([sz, dm]) => (
                                                    <button key={sz} type="button" onClick={() => handleBtnSize(sz)}
                                                        style={{ flex: 1, padding: "5px 8px", borderRadius: 7, border: buttonSize === sz ? "1.5px solid var(--btn-color)" : "1.5px solid var(--body-color-less2)", background: buttonSize === sz ? "color-mix(in srgb,var(--btn-color) 10%,transparent)" : "var(--body-color)", color: buttonSize === sz ? "var(--btn-color)" : "var(--text-color)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{dm}</button>
                                                ))}
                                            </div>
                                        )}
                                        {activeTab === "qr" ? (
                                            <div style={{ textAlign: "center", paddingBottom: 4 }}>
                                                <SimpleQR value={getLink()} size={140} />
                                                <div style={{ fontSize: 11, color: "var(--text-color)", opacity: .4, marginTop: 8 }}>Scan to pay</div>
                                                <button onClick={() => copyText(getQrHtml())} style={{ marginTop: 10, width: "100%", padding: 7, borderRadius: 7, border: "1px solid var(--btn-color)", background: "transparent", color: "var(--btn-color)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}><i className="mdi mdi-content-copy" /> Copy Embed Code</button>
                                            </div>
                                        ) : (<>
                                            <textarea ref={textareaRef} readOnly value={htmlCode} style={{ width: "100%", minHeight: 80, padding: "10px 12px", borderRadius: 8, border: "1px solid var(--body-color-less2)", background: "var(--body-color)", color: "var(--text-color)", fontSize: 11, fontFamily: "'SF Mono',monospace", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
                                            <button onClick={() => copyText(htmlCode)} style={{ marginTop: 8, width: "100%", padding: 7, borderRadius: 7, border: "1px solid var(--btn-color)", background: "transparent", color: "var(--btn-color)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}><i className="mdi mdi-content-copy" /> Copy Code</button>
                                        </>)}
                                        <div style={{ marginTop: 12, borderTop: "1px solid var(--body-color-less2)", paddingTop: 12 }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".6px", color: "var(--text-color)", opacity: .4, marginBottom: 8 }}>Preview</div>
                                            <div style={{ padding: 14, borderRadius: 8, border: "1px dashed var(--body-color-less2)", background: "var(--body-color)", textAlign: "center", minHeight: 52, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                {activeTab === "link" && <a href={getLink()} target="_blank" rel="noreferrer" style={{ color: "#818cf8", fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>{getLink()}</a>}
                                                {activeTab === "button" && <a href={getLink()} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: buttonSize === "small" ? 80 : buttonSize === "large" ? 132 : 105, height: buttonSize === "small" ? 43 : buttonSize === "large" ? 70 : 56, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff", borderRadius: 6, fontWeight: 700, fontSize: buttonSize === "small" ? 11 : 13, textDecoration: "none" }}>Pay Now</a>}
                                                {activeTab === "hyperlink" && <a href={getLink()} target="_blank" rel="noreferrer" style={{ color: "var(--btn-color)", fontWeight: 600, fontSize: 14 }}>Pay Now</a>}
                                                {activeTab === "qr" && <SimpleQR value={getLink()} size={100} />}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Pre-generate placeholder */}
                                {!generated && (
                                    <div style={{ ...RIGHT_CARD, textAlign: "center", padding: "28px 18px" }}>
                                        <div style={{ width: 44, height: 44, borderRadius: 12, background: "color-mix(in srgb,var(--btn-color) 10%,transparent)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", fontSize: 20, color: "var(--btn-color)" }}>
                                            <i className="mdi mdi-magic-staff" />
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-color)", marginBottom: 5 }}>Ready to generate</div>
                                        <div style={{ fontSize: 12, color: "var(--text-color)", opacity: .4, lineHeight: 1.6 }}>Fill in a payment name and required profiles, then hit <strong>Create Subscription Link</strong>.</div>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginTop: 14 }}>
                                            {["Link", "Button", "QR Code", "Hyperlink"].map(f => (
                                                <span key={f} style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: "color-mix(in srgb,var(--btn-color) 8%,transparent)", color: "var(--btn-color)", border: "1px solid color-mix(in srgb,var(--btn-color) 15%,transparent)" }}>{f}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>

            {/* Sticky CTA */}
            <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "var(--body-color-less)", borderTop: "1px solid var(--body-color-less2)", padding: "12px 24px", zIndex: 9, backdropFilter: "blur(8px)" }}>
                <div className="cp-bar" style={{ maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
                    {generated && (
                        <button className="cp-bar-btn" onClick={() => copyText(getLink())}
                            style={{ padding: "10px 22px", borderRadius: 10, border: "1.5px solid var(--btn-color)", background: "transparent", color: "var(--btn-color)", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <i className="mdi mdi-content-copy" /> Copy Link
                        </button>
                    )}
                    <button className="cp-bar-btn" disabled={!canGenerate || loading} onClick={handleGenerate}
                        style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: canGenerate ? "var(--btn-color)" : "var(--body-color-less2)", color: canGenerate ? "#fff" : "var(--text-color)", opacity: canGenerate ? (loading ? .7 : 1) : .5, fontSize: 13, fontWeight: 600, cursor: canGenerate && !loading ? "pointer" : "not-allowed", display: "inline-flex", alignItems: "center", gap: 6, transition: "all .2s" }}>
                        {loading ? <><i className="mdi mdi-loading mdi-spin" /> Generating…</> : <><i className="mdi mdi-magic-staff" /> {generated ? "Regenerate Link" : "Create Subscription Link"}</>}
                    </button>
                </div>
            </div>
        </Fragment>
    );
}